import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { FeedstockService } from '../../../project/feedstock/feedstock.service';
import Utils from '../../../util';
import { FeedstockBreakdownDataItem } from '../../../models/data-structures/results/feedstock-breakdown/feedstock-breakdown-data-item.model';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import FeedstockUtil from '../../../project/feedstock/feedstock.util';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { CommunicationService } from '../../../communication/communication.service';
import { Project, ProjectInterface } from '../../../models/project/project.model';
import { Results, ResultsInterface } from '../../../models/results/results.model';
import { Expenses } from '../../../models/results/financial/ispl/ispl.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { Revenues } from '../../../models/results/financial/ispl/revenues.model';
import { EarningsKeys } from '../../../models/results/financial/ispl/earnings.model';
import { PlotlyTrace, YearData } from '../../../models/results/plotly.model';
import { Scenario, ScenarioInterface } from '../../../models/results/scenario-comparison/scenario.model';

export class DisplayData {
  public hasGraphData: boolean;
  public graphData?: PlotlyTrace[];
  public hasTableData: boolean;
  public tableData?: { label: string, values: number[] }[];
  public tableHeaders?: string[];

  constructor() {
    this.hasGraphData = false;
    this.hasTableData = false;
  }

  setGraphData(graphData: any[]) {
    this.hasGraphData = true;
    this.graphData = graphData;
  }

  setTableData(tableData: { label: string, values: number[] }[], tableHeaders: string[]) {
    this.hasTableData = true;
    this.tableData = tableData;
    this.tableHeaders = tableHeaders;
  }
}

@Injectable({
  providedIn: 'root'
})
/**
 * A service that contains functionality for creating the feedstock breakdown data.
 */
export class ScenarioResultsService implements OnDestroy {
  // notifiers

  /**
   * A notifier that is true when the service is ready to be used.
   */
  serviceReadyNotifier = new BehaviorSubject(false);
  /**
   * A notifier that is emitted when the service is destroyed; used to clean up subscriptions created in the class.
   */
  private onDestroyNotifier = new EventEmitter();

  // private properties

  /**
   * A unit for the metric version of the data; ton/day.
   */
  private metricUnit: Unit | null = null;
  /**
   * A unit for the imperial version of the data; short ton/day.
   */
  private imperialUnit: Unit | null = null;

  // computational properties

  /**
   * An array of the current feedstocks in the project.
   */
  private feedstocks?: Feedstock[];
  /**
   * The value of the total mass of all the current feedstocks in ton/day
   */
  private totalMassInTonPerDay?: number;
  /**
   * The data array of feedstock breakdown data.
   */
  private feedstockBreakdownData?: FeedstockBreakdownDataItem[];

  // boolean notifier states

  /**
   * Boolean that determines if the feedstock service is ready.
   */
  private feedstockServiceReady = false;
  /**
   * Boolean that determines if the project communication service is ready.
   */
  private projCommServiceReady = false;
  /**
   * Boolean that determines if the unit service is ready.
   */
  private unitServiceReady = false;
  private baselineResultsLoaded = false;
  private scenarioResultsLoaded = false;

  private baselineResultsSet?: Results;
  private processedScenarios?: Scenario[];
  private projectState?: Project;


  /**
   * Constructor; Injects the FeedstockService, UnitService, ProjectCommunicationService.
   * @param feedstockService is the FeedstockService; Used for setting the feedstocks array and getting
   * the mass of each feedstock.
   * @param unitService is the UnitService; Used to fetch and convert units.
   * @param projectCommService is the ProjectCommunicationService; Used to get the precision set by the user.
   * @param toastrService is the ConfiguredToastrService; Used to relay messages to the user.
   * @param translationService
   * @param communicationService
   */
  constructor(private feedstockService: FeedstockService,
              private unitService: UnitService,
              private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private translationService: AnessaTranslationService,
              private communicationService: CommunicationService
  ) {
    this.feedstockService.feedstockServiceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.feedstockServiceReady = loaded;
        this.updateNotifier();
        if (loaded) {
          this.feedstocks = this.feedstockService.getFeedstocks();
          this.computeFeedstockData();
        }
      });

    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.projCommServiceReady = loaded;
        this.loadBaselineResults(this.projectCommService.getScenarioComparisonResultId());
        this.loadScenarioResults();
        this.updateNotifier();
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitServiceReady = loaded;
        this.updateNotifier();
        if (loaded) {
          this.metricUnit = this.unitService.getUnitById('000000000000000000004901');
          this.imperialUnit = this.unitService.getUnitById('000000000000000000004902');
        }
      });
  }

  /**
   * Emits, and unsubscribes, from the onDestroyNotifier.
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  getBaselineResults() {
    if (!this.baselineResultsSet) {
      throw Error('baseline results not loaded');
    }
    return Utils.deepCopy(this.baselineResultsSet);
  }

  getProcessScenarios() {
    if (!this.processedScenarios) {
      throw Error('processed scenarios have not be loaded');
    }
    const copy: Scenario[] = [];
    for (const item of this.processedScenarios) {
      copy.push(item.deepCopy());
    }
    return copy;
  }

  getResultProject() {
    return this.projectState;
  }

  processScenarioFeedstocks(feedstocks: Feedstock[]) {
    if (feedstocks) {
      this.feedstocks = [...feedstocks];
      this.computeFeedstockData();
    }
  }

  /**
   * Get the share of the provided feedstock, as a percentage.
   * @param feedstock is the feedstock for which the share will be calculated.
   */
  getFeedstockShare(feedstock: Feedstock): number | null {
    if (feedstock && feedstock.quantities && this.totalMassInTonPerDay) {
      const massInTonPerDay = FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService);
      const share = massInTonPerDay / this.totalMassInTonPerDay;
      return Utils.roundToPrecision(share * 100, this.projectCommService.getPrecision());
    }
    return null;
  }

  /**
   * Get the feedstock breakdown data.
   */
  getFeedstockBreakdownData(): FeedstockBreakdownDataItem[] | undefined {
    return this.feedstockBreakdownData;
  }

  /**
   * Get the feedstock breakdown data with imperial values.
   */
  convertFeedstockBreakdownDataToImperial(data: FeedstockBreakdownDataItem[]): FeedstockBreakdownDataItem[] {
    if (!this.feedstockBreakdownData) {
      throw Error('unable to get feedstock breakdown data with imperial values');
    }
    const convertedData: FeedstockBreakdownDataItem[] = Utils.deepCopy(data);
    for (const item of convertedData) {
      const numericValue = Utils.removeCommas(item.amount);
      const convertedValue = this.metricUnit.convertToUnit(numericValue, this.imperialUnit);
      item.amount = Utils.addCommas(convertedValue);
      item.unit = 'short ton/day';
    }
    return convertedData;
  }

  getProjectDecimalPrecision(): number | null {
    return this.projectCommService.getPrecision();
  }

  /**
   * **************************************************
   * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
   * ~~~~~~~~~~ FINANCIAL GENERAL SERVICE ~~~~~~~~~~~~~
   * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
   * **************************************************
   */

  getOpexBreakdown(selectedCurrency: Unit, resultSet: Results) {
    const displayData = new DisplayData();

    const opexExpenses = {
      'RESULTS.FINANCIAL_GENERAL.FEEDSTOCK_EXPENSES': 'RESULTS.DATA.ISPL.FE',
      'RESULTS.FINANCIAL_GENERAL.DIGESTATE_EXPENSES': 'RESULTS.DATA.ISPL.DE',
      'RESULTS.FINANCIAL_GENERAL.CONSUMED_FUELS': 'RESULTS.DATA.ISPL.UCF',
      'RESULTS.FINANCIAL_GENERAL.LABOUR': 'RESULTS.DATA.ISPL.LABOUR',
      'RESULTS.FINANCIAL_GENERAL.MAINTENANCE': 'RESULTS.DATA.ISPL.MAINTENANCE',
      'RESULTS.FINANCIAL_GENERAL.MAJOR_MAINTENANCE': 'RESULTS.DATA.ISPL.PMM',
      'RESULTS.FINANCIAL_GENERAL.CONSUMABLES': 'RESULTS.DATA.ISPL.CONSUMABLES',
      'RESULTS.FINANCIAL_GENERAL.INSURANCE': 'RESULTS.DATA.INSURANCE',
    };
    if (!resultSet?.financial?.ispl) {
      throw Error('no results provided for opex breakdown');
    }
    const plStatement = resultSet.financial.ispl;

    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [averageHeader, shareHeader];

    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalOpex = 0;
    for (const opexName of Object.keys(opexExpenses)) {
      const opexKey = opexExpenses[opexName as keyof typeof opexExpenses];
      const key = opexKey as keyof Expenses;
      let expenseAverage = 0;
      const subTable = plStatement.expenses[key];
      if (!subTable) continue;

      for (const result of Object.keys(subTable)) {
        const subKey = result as keyof typeof subTable;
        const data: ResultRow = subTable[subKey];
        let total = 0;
        for (let i = 1; i < data.values.length; i++) {
          total += Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
        }
        const average = total / (data.values.length - 1) * -1;
        expenseAverage += average;
      }
      totalOpex += expenseAverage;
      graphData.push({ name: opexName, value: expenseAverage });
      tableData.push({ label: opexName, values: [expenseAverage] });
    }

    for (const data of tableData) {
      if (totalOpex === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalOpex * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getCapexBreakdown(selectedCurrency: Unit, dataset: { project: Project, results: Results }): DisplayData {
    const displayData = new DisplayData();
    const project = dataset.project;
    const results = dataset.results;
    if (!project || !project.financial || !project.financial.capex || project.financial.capex.length < 1) {
      throw Error('invalid project provided for capex breakdown results');
    }

    const capex = project.financial.capex;

    const costHeader = this.translationService.get('RESULTS.GENERAL.COST');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [costHeader, shareHeader];

    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalCapex = 0;
    for (const expense of capex) {
      let convertedValue;
      if (expense.connectionToggle) {
        const rate = results.technical.product_rates[expense.connectionType];
        const periodInDays = this.unitService.getValueInBaseUnit(expense.period);
        const amount = this.unitService.getValueInBaseUnit(expense.quantity);
        const cost = expense.cost.value * (rate * periodInDays / amount);
        convertedValue = Unit.convertValue(cost, expense.cost.unit, selectedCurrency.id);
      } else {
        convertedValue = Unit.convertValue(expense.cost.value, expense.cost.unit, selectedCurrency.id);
      }
      totalCapex += convertedValue;

      graphData.push({name: expense.name, value: convertedValue});
      tableData.push({label: expense.name, values: [convertedValue]});
    }

    for (const data of tableData) {
      if (totalCapex === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalCapex * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getRevenuesBreakdown(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();
    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for revenues breakdown');
    }
    const plStatement = results.financial.ispl;

    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [averageHeader, shareHeader];

    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalRevenue = 0;
    for (const type of Object.keys(plStatement.revenues)) {
      const revenuesType = type as keyof Revenues;
      let average = 0;
      const subTable = plStatement.revenues[revenuesType];
      if (!subTable) continue;
      for (const income of Object.keys(subTable)) {
        const data = subTable[income];
        // Detect variable pricing structure and adjust accordingly
        if (!('RESULTS.DATA.VALUES' in data)) {
          if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
          for (const breakdown in data) {
            if (data[breakdown] instanceof ResultRow) {
              let total = 0;
              for (let i = 1; i < data[breakdown].values.length; i++) {
                total += Unit.convertValue(data[breakdown].values[i], data[breakdown].unit, selectedCurrency.id);
              }
              average += total / (data[breakdown].values.length - 1);
            }
          }
        } else if (data instanceof ResultRow) {
          let total = 0;
          for (let i = 1; i < data.values.length; i++) {
            total += Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
          }
          average += Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
        }
      }
      totalRevenue += average;

      graphData.push({name: type, value: average});
      tableData.push({label: type, values: [average]});
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getSellingProductsRevenueBreakdown(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();
    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for selling products revenue breakdown');
    }
    const plStatement = results.financial.ispl;
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [averageHeader, shareHeader];

    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalRevenue = 0;
    let average = 0;
    for (const income of Object.keys(plStatement.revenues.sp)) {
      const data = plStatement.revenues.sp[income];
      // Detect variable pricing structure and adjust accordingly
      if (!('RESULTS.DATA.VALUES' in data)) {
        if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
        for (const breakdown in data) {
          if (data[breakdown] instanceof ResultRow) {

            let total = 0;
            for (let i = 1; i < data[breakdown].values.length; i++) {
              total += data[breakdown].values[i];
            }
            average = Unit.convertValue(total, data[breakdown].unit, selectedCurrency.id) / (data[breakdown].values.length - 1);
            totalRevenue += average;

            const labelTranslation = this.translationService.get(data[breakdown].label);
            const combinedName = `${income} - ${labelTranslation}`;
            graphData.push({name: combinedName, value: average});
            tableData.push({label: combinedName, values: [average]});
          }
        }
      } else if (data instanceof ResultRow) {
        let total = 0;
        for (let i = 1; i < data.values.length; i++) {
          total += data.values[i];
        }
        average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
        totalRevenue += average;
        graphData.push({name: income, value: average});
        tableData.push({label: income, values: [average]});
      }
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getFeedstockRevenueBreakdown(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();
    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for feedstock revenue breakdown');
    }
    const plStatement = results.financial.ispl;
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [averageHeader, shareHeader];

    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalRevenue = 0;
    for (const income of Object.keys(plStatement.revenues.ftf)) {
      const data = plStatement.revenues.ftf[income];
      let total = 0;
      for (let i = 1; i < data.values.length; i++) {
        total += data.values[i];
      }
      const average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
      totalRevenue += average;

      if (average !== 0) {
        graphData.push({name: income, value: average});
        tableData.push({label: income, values: [average]});
      }
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getAdditionalIncomeBreakdown(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();

    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for additional income breakdown');
    }
    const plStatement = results.financial.ispl;
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE');
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [averageHeader, shareHeader];


    const graphData: { name: string, value: number }[] = [];
    const tableData: { label: string, values: number[]}[] = [];

    let totalRevenue = 0;
    for (const income of Object.keys(plStatement.revenues.ai)) {
      const data = plStatement.revenues.ai[income];
      let total = 0;
      for (let i = 1; i < data.values.length; i++) {
        total += data.values[i];
      }
      const average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
      totalRevenue += average;

      if (average !== 0) {
        graphData.push({name: income, value: average});
        tableData.push({label: income, values: [average]});
      }
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data.values[shareHeader] = this.translationService.get('SENSITIVITY.INPUT_SELECTION.N_A');
      } else {
        data.values[shareHeader] = `${Utils.roundToPrecision(data.values[0] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getRevenueTrend(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();
    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for revenue trend');
    }
    const plStatement = results.financial.ispl;
    const graphData: PlotlyTrace[] = [];
    for (const type of Object.keys(plStatement.revenues)) {
      const trace: PlotlyTrace = {x: [], y: [], name: type, type: 'bar'};
      const revenuesType = type as keyof Revenues;
      const subTable = plStatement.revenues[revenuesType];
      if (!subTable) continue;
      for (const income of Object.keys(subTable)) {
        const data = subTable[income];
        // Detect variable pricing structure and adjust accordingly
        if (!('RESULTS.DATA.VALUES' in data)) {
          if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
          for (const breakdown of Object.keys(data)) {
            for (let i = 0; i < data[breakdown].values.length; i++) {
              if (!trace.x.includes(i)) {
                trace.x[i] = i;
                trace.y[i] = 0;
              }
              const value = Unit.convertValue(data[breakdown].values[i], data[breakdown].unit, selectedCurrency.id);
              trace.y[i] += Utils.roundToPrecision(value, this.projectCommService.getPrecision());
            }
          }
        } else if (data instanceof ResultRow) {
          for (let i = 0; i < data.values.length; i++) {
            if (!trace.x.includes(i)) {
              trace.x[i] = i;
              trace.y[i] = 0;
            }
            trace.y[i] += Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
          }
        }
      }
      graphData.push(trace);
    }

    displayData.setGraphData(graphData);

    return displayData;
  }

  getEarningsTrend(selectedCurrency: Unit, results: Results): DisplayData {
    const displayData = new DisplayData();
    if (!results?.financial?.ispl) {
      throw Error('invalid results provided for earnings trend');
    }
    const plStatement = results.financial.ispl;

    let lifetime = 0;
    for (const key of Object.keys(plStatement.earnings)) {
      const myKey = key as EarningsKeys;
      const earningType = plStatement.earnings[myKey];
      const subKey = key as keyof typeof earningType;
      const data: ResultRow = earningType[subKey];
      if (data.values.length > lifetime) {
        lifetime = data.values.length;
      }
    }

    const graphData: { name: string, series: { name: string, value: number }[] }[] = [];
    for (let i = 0; i < lifetime; i++) {
      const yearData: YearData = {name: String(i), series: []};
      for (const key of Object.keys(plStatement.earnings)) {
        const myKey = key as EarningsKeys;
        const earningType = plStatement.earnings[myKey];
        const subKey = key as keyof typeof earningType;
        const data: ResultRow = earningType[subKey];
        yearData.series.push({
          name: data.label,
          value: Unit.convertValue(data.values[i], data.unit, selectedCurrency.id)
        });
      }
      graphData.push(yearData);
    }

    displayData.setGraphData(graphData);

    return displayData;
  }

  /**
   * Load the results that were used for the scenario results simulation
   * @param resultId the results id for the baseline results used to simulate the scenarios
   * @private
   */
  private loadBaselineResults(resultId: string) {
    if (!resultId) {
      this.toastrService.warning(this.translationService.get('SCENARIO_COMPARISON.RESULTS.NO_SCENARIO_RESULTS'));
      return;
    }
    const projectID = localStorage.getItem('projectID');
    const postpack = {projectId: projectID, resultId: resultId};
    (this.communicationService.postRequest(postpack,
      '/wsasapi/results/getResultFromHistById') as Observable<{obj: {
        results: ResultsInterface,
        'project-state': ProjectInterface
      }}>)
      .pipe(take(1))
      .subscribe(res => {
        if (res && res.obj && res.obj.results) {
          this.baselineResultsSet = new Results(res.obj.results);
          this.projectState = new Project(res.obj['project-state']);
          this.baselineResultsLoaded = true;
          this.updateNotifier();
        } else {
          throw Error('no baseline results found for scenario comparison');
        }
      });
  }

  /**
   * Loads the current scenarios with results that have had java translation ids that have had '~' replaced with '.'
   * @private
   */
  private loadScenarioResults() {
    const projectID = localStorage.getItem('projectID');
    const postpack = { projectId: projectID };
    (this.communicationService.postRequest(postpack,
      '/wsasapi/scenariocomparison/getProcessScenarios') as Observable<{obj: ScenarioInterface[]}>)
      .pipe(take(1))
      .subscribe(res => {
        if (res && res.obj) {
          this.processedScenarios = [];
          for (const item of res.obj) {
            this.processedScenarios.push(new Scenario(item));
          }
          this.scenarioResultsLoaded = true;
          this.updateNotifier();
        } else {
          throw Error('failed to fetch scenarios');
        }
      });
  }

  /**
   * Check all the service ready properties and update the feedstock breakdown service notifier accordingly.
   */
  private updateNotifier(): void {
    this.serviceReadyNotifier.next(this.feedstockServiceReady && this.projCommServiceReady &&
      this.unitServiceReady && this.baselineResultsLoaded && this.scenarioResultsLoaded);
  }

  /**
   * Computes data that will be provided by the service.
   */
  private computeFeedstockData(): void {
    this.calculateTotalMassInTonsPerDay();
    this.setFeedstockBreakdownData();
  }

  /**
   * Calculate, and set, the total mass, in ton/day, of all feedstocks
   */
  private calculateTotalMassInTonsPerDay(): void {
    if (!this.feedstocks) return;

    this.totalMassInTonPerDay = 0;
    for (const feedstock of this.feedstocks) {
      this.totalMassInTonPerDay += FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService);
    }
  }

  /**
   * Create, and set, the feedstock breakdown data.
   */
  private setFeedstockBreakdownData() {
    this.feedstockBreakdownData = [];
    if (!this.feedstocks) {
      this.toastrService.error(this.translationService.get('SCENARIO_COMPARISON.RESULTS.FEEDSTOCK_UNAVAILABLE'));
      return;
    }
    if (this.feedstocks.length < 1) {
      this.toastrService.warning(
        this.translationService.get('SCENARIO_COMPARISON.RESULTS.MISSING_FEEDSTOCK'),
        this.translationService.get('RESULTS.ERRORS.NO_FEEDSTOCKS_FOUND')
      );
    }
    for (const feedstock of this.feedstocks) {
      const name = feedstock.name;
      const amount = Utils.addCommas(FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService));
      const unit = 'tonne/day';
      const share = this.getFeedstockShare(feedstock) + '%';
      this.feedstockBreakdownData.push(new FeedstockBreakdownDataItem(name, amount, unit, share));
    }
  }

  scenarioComparisonHasResults() {
    if (!this.projCommServiceReady) {
      throw Error('scenario comparison has results check not waiting for project to be loaded');
    }
    const scenarioComparison = this.projectCommService.getScenarioComparison();
    // needs baseline result id
    if (!scenarioComparison || !scenarioComparison.resultHistoryID) {
      // scenarios need to be simulated
      this.toastrService.warning(this.translationService.get('SCENARIO_COMPARISON.RESULTS.NO_BASELINE_RESULTS'),
        this.translationService.get('SCENARIO_COMPARISON.RESULTS.RESULTS_INCOMPLETE'));
      return false;
    }
    // needs at least one scenario
    if (!scenarioComparison.scenarios || scenarioComparison.scenarios.length < 1) {
      // no scenarios
      // TODO error message here
      return false;
    }

    return true;
  }
}
